import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type { TaskStatus, TicketStatus } from '@/types/database';

/**
 * Hooks for the in-app notification system (migration 93).
 *
 * Two families of rows land here:
 *   * @mentions — someone tagged the current user in a task comment,
 *     task description, project note or ticket comment.
 *   * Tickets (migration 143) — a request was sent to you, routed to
 *     you, changed status, or picked up a reply. These are inserted by
 *     SECURITY DEFINER triggers on the ticket tables.
 * Every query is implicitly scoped to the signed-in user by RLS
 * (recipient_id = auth.uid()), so we never filter by recipient on the
 * client.
 *
 * Rows are never inserted directly from the client — mention fan-out
 * goes through the create_mention_notifications /
 * create_ticket_mention_notifications RPCs, which validate that each
 * tag target can actually access the project / ticket.
 */

export type NotificationSourceType =
  | 'task_comment'
  | 'project_note'
  | 'task_description'
  | 'ticket'
  | 'ticket_event'
  | 'ticket_comment';

/** `notifications.type` values written for tickets (migration 143). */
export type TicketNotificationType =
  | 'ticket_new'
  | 'ticket_assigned'
  | 'ticket_routed'
  | 'ticket_status'
  | 'ticket_comment'
  | 'ticket_on_behalf';

/** A notification joined with the actor + light project / task / ticket context. */
export interface NotificationWithContext {
  id: string;
  organization_id: string;
  recipient_id: string;
  actor_id: string | null;
  type: string;
  source_type: NotificationSourceType;
  source_id: string | null;
  project_id: string | null;
  task_id: string | null;
  ticket_id: string | null;
  snippet: string | null;
  read_at: string | null;
  created_at: string;
  actor: {
    id: string;
    full_name: string | null;
    email: string;
    avatar_url: string | null;
  } | null;
  project: { id: string; name: string } | null;
  task: { id: string; name: string; status: TaskStatus } | null;
  ticket: { id: string; number: number; title: string; status: TicketStatus } | null;
}

export function isTicketNotification(n: Pick<NotificationWithContext, 'ticket_id' | 'source_type'>): boolean {
  return !!n.ticket_id || n.source_type.startsWith('ticket');
}

/** Where a notification should take you when clicked. */
export function notificationHref(n: NotificationWithContext): string {
  if (n.ticket_id) return `/tickets/${n.ticket_id}`;
  if (n.project_id && n.task_id) {
    return `/projects/${n.project_id}?task=${n.task_id}`;
  }
  // Legacy project_note mentions (the notes tab was retired with the
  // Tasks rename) still land on the project.
  if (n.project_id) return `/projects/${n.project_id}`;
  return '/notifications';
}

/** Short human label for what the notification is about. */
export function notificationContextLabel(n: NotificationWithContext): string {
  switch (n.source_type) {
    case 'task_comment':
      return n.task ? `a comment on “${n.task.name}”` : 'a comment';
    case 'task_description':
      return n.task ? `the description of “${n.task.name}”` : 'a task description';
    case 'project_note':
      return n.project ? `a note in ${n.project.name}` : 'a project note';
    case 'ticket':
    case 'ticket_event':
    case 'ticket_comment':
      return n.ticket ? `ticket ${ticketRefOf(n.ticket.number)}` : 'a ticket';
    default:
      return 'a project item';
  }
}

function ticketRefOf(number: number): string {
  return `T-${String(number).padStart(4, '0')}`;
}

/**
 * The sentence after the actor's name: "tagged you in a comment on …",
 * "sent you a request", "moved T-0042 to On order". The row renders the
 * actor in bold and this in muted text.
 */
export function notificationHeadline(n: NotificationWithContext): string {
  const ref = n.ticket ? ticketRefOf(n.ticket.number) : 'a ticket';
  switch (n.type as TicketNotificationType | 'mention') {
    case 'ticket_new':
      return n.actor_id ? `sent you a request · ${ref}` : `filed a request · ${ref}`;
    case 'ticket_assigned':
      return `routed ${ref} to you`;
    case 'ticket_routed':
      return `routed ${ref}${n.snippet?.startsWith('Routed to ') ? ` to ${n.snippet.slice('Routed to '.length)}` : ''}`;
    case 'ticket_status':
      return `updated ${ref}`;
    case 'ticket_comment':
      return `replied on ${ref}`;
    case 'ticket_on_behalf':
      return `filed ${ref} on your behalf`;
    case 'mention':
    default:
      return `tagged you in ${notificationContextLabel(n)}`;
  }
}

/**
 * Whether the snippet should be shown under the headline. Routing
 * notifications already fold their snippet into the headline.
 */
export function notificationShowsSnippet(n: NotificationWithContext): boolean {
  if (!n.snippet) return false;
  return n.type !== 'ticket_routed';
}

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (filter: NotificationListFilter) =>
    [...notificationKeys.all, 'list', filter] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

export type NotificationListFilter = 'all' | 'unread';

const SELECT_WITH_CONTEXT =
  'id, organization_id, recipient_id, actor_id, type, source_type, source_id, project_id, task_id, ticket_id, snippet, read_at, created_at, ' +
  'actor:actor_id(id, full_name, email, avatar_url), ' +
  'project:project_id(id, name), ' +
  'task:task_id(id, name, status), ' +
  'ticket:ticket_id(id, number, title, status)';

// How often the topbar bell re-checks. Notifications are low-volume
// (@mentions and ticket activity), so a gentle poll keeps things fresh;
// the ticket realtime channel invalidates this on top when it is open.
const POLL_MS = 60_000;

/** Full list for the inbox page. `filter` toggles unread-only. */
export function useNotifications(
  filter: NotificationListFilter = 'all',
  options?: { enabled?: boolean },
) {
  const { profile } = useAuth();
  const enabled = !!profile && (options?.enabled ?? true);
  return useQuery({
    queryKey: notificationKeys.list(filter),
    enabled,
    refetchInterval: POLL_MS,
    queryFn: async () => {
      let query = supabase
        .from('notifications')
        .select(SELECT_WITH_CONTEXT)
        .order('created_at', { ascending: false })
        .limit(200);
      if (filter === 'unread') query = query.is('read_at', null);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as NotificationWithContext[];
    },
  });
}

/** Unread count for the topbar badge. Polls on the same cadence. */
export function useUnreadNotificationCount() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    enabled: !!profile,
    refetchInterval: POLL_MS,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .is('read_at', null);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; read: boolean }) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: input.read ? new Date().toISOString() : null })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async () => {
      if (!profile) throw new Error('Not signed in.');
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('notifications').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Fan out mention notifications for a just-saved comment / note /
 * description. Safe to call with an empty recipient list (no-op) and
 * safe to re-run on edit — the RPC de-dupes per (recipient, source).
 *
 * After the inbox rows land, we best-effort invoke notify-deliver so
 * email + Slack fire even when the DB trigger can't reach pg_net
 * (local supabase without vault secrets). The Edge Function claims
 * email_sent_at / slack_sent_at, so a trigger + client double-invoke
 * cannot double-send.
 *
 * Notification creation is best-effort from the caller's perspective:
 * the source row (comment/note) is already persisted, so a failure here
 * shouldn't surface as a hard error on the composer. Callers should
 * catch + swallow (or soft-toast) rather than block the save.
 */
export function useCreateMentionNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      recipientIds: string[];
      projectId: string;
      sourceType: NotificationSourceType;
      sourceId: string | null;
      taskId?: string | null;
      snippet?: string | null;
    }) => {
      const recipients = Array.from(new Set(input.recipientIds)).filter(Boolean);
      if (recipients.length === 0) return 0;
      const { data, error } = await supabase.rpc('create_mention_notifications', {
        p_recipient_ids: recipients,
        p_project_id: input.projectId,
        p_source_type: input.sourceType,
        p_source_id: input.sourceId,
        p_task_id: input.taskId ?? null,
        p_snippet: input.snippet ?? null,
      });
      if (error) throw error;
      const ids = Array.isArray(data) ? (data as string[]) : [];
      if (ids.length > 0) {
        void supabase.functions
          .invoke('notify-deliver', { body: { notification_ids: ids } })
          .then(({ error: deliverErr }) => {
            if (deliverErr) console.warn('notify-deliver failed', deliverErr);
          });
      }
      return ids.length;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export type NotificationDeliveryChannelResult = 'sent' | 'skipped' | 'error';

export interface NotificationDeliveryTestResult {
  ok: boolean;
  email: NotificationDeliveryChannelResult;
  slack: NotificationDeliveryChannelResult;
  email_error?: string;
  slack_error?: string;
}

/** Super-admin test ping: emails the caller and posts to the org Slack webhook. */
export function useTestNotificationDelivery() {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('notify-deliver', {
        body: { test: true },
      });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const body = (await ctx.clone().json()) as { error?: string };
            throw new Error(body?.error ?? error.message);
          } catch (inner) {
            if (inner instanceof Error && inner.message !== error.message) throw inner;
          }
        }
        throw new Error(error.message);
      }
      return data as NotificationDeliveryTestResult;
    },
  });
}
